import { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { FeatureContext, FeatureFlags, FeatureId } from './helpers';
import useApp from '@/hooks/use-app.hook';
import useAppData from '@/hooks/use-app-data.hook';
import { AppDataQueryKey } from '@/lib/query-client';
import useQueryHelper from '@/hooks/use-query-helper';
import { getUrlQueryParam } from '@/lib/url-helpers';
import { userCan } from '@/lib/services/auth.service';
import { has } from 'lodash';
import { AdminUserPermissions } from '@/lib/services/admin-user.service';
import Environment from '@/lib/environment';
import { OrgBasicInfo } from '@/lib/models/org';

interface Props {
  children: ReactNode;
}

const { METABASE_ENABLED } = Environment;

export const FeatureProvider: FC<Props> = ({ children }) => {
  const { user, signedIn } = useApp();

  const orgBasicInfoQuery = useAppData<OrgBasicInfo | null>(AppDataQueryKey.OrgBasicInfo, {
    enabled: signedIn,
  });
  const { data: orgBasicInfo } = orgBasicInfoQuery;
  const { showLoader: orgLoading } = useQueryHelper(orgBasicInfoQuery);

  const adminUserPermQuery = useAppData<AdminUserPermissions | null>(
    AppDataQueryKey.AdminUserPermissions,
    {
      enabled: signedIn,
    }
  );
  const { data: adminUserPermData } = adminUserPermQuery;
  const { showLoader: adminUserPermLoading } = useQueryHelper(adminUserPermQuery);

  const [qsFlags] = useState({
    isDemo: !!getUrlQueryParam('demo'),
    force: !!getUrlQueryParam('force'),
  });

  const hasAssume = !!user?.assumed;
  const orgMode = orgBasicInfo?.mode || 'platform';
  const canWrite = userCan(user, 'write');
  const isAuditor = user?.role === 'org:auditor';
  const canManageAdmins = userCan(user, 'manage-admins');
  const canConfigureConversationExport = userCan(user, 'configure-conversation-export');
  const { canViewConversations } = adminUserPermData || {};

  const flags: FeatureFlags = useMemo(() => {
    const { isDemo, force } = qsFlags;

    const hasPolicyV2 = orgBasicInfo?.policyEngine.version === 'v2';

    return {
      canViewConnectorArn: true,
      canChangePrivateModels: canWrite,
      canChangeAuth: canWrite,
      canChangeSync: canWrite,
      canChangePolicy: canWrite,
      canManageAdmins,
      canChangeGroups: canWrite,
      canChangeUsers: canWrite,
      canViewConversations: !hasAssume && !!canViewConversations,
      canChangeCustomMessage: canWrite,
      canChangeBranding: canWrite,
      canChangeContextProviders: canWrite,
      canChangeAgents: canWrite,
      canChangeClassifiers: canWrite,
      canChangeTelemetry: canWrite,
      canChangeConnector: canWrite,
      hasDemoUserEventFilter: isDemo,
      canViewConfidentialDatasources: isDemo,
      canViewCharts: !!METABASE_ENABLED || force,
      canViewDemoConnectors: isDemo,
      canViewReroute: true, //TODO: Remove when we are comfortable with the feature
      hasPolicyV1: !hasPolicyV2,
      hasPolicyV2,
      hasAssume,
      hasIntents: !isDemo,
      hasClassifiers: true,
      canConfigureConversationExport,
    };
  }, [
    canWrite,
    canManageAdmins,
    canConfigureConversationExport,
    hasAssume,
    qsFlags,
    canViewConversations,
    orgBasicInfo,
  ]);

  const hasFeature = useCallback(
    (featureFlagVal: string): boolean => {
      if (!has(flags, featureFlagVal)) {
        return false;
      }

      return !!flags[featureFlagVal];
    },
    [flags]
  );

  const getTooltip = useCallback(
    (feature: FeatureId) => {
      if (feature === 'view-conversation') {
        if (hasAssume) {
          return 'Conversation history is not available when assuming a support role';
        }

        if (!canViewConversations) {
          return 'You do not have permission to view conversation history.';
        }
      }

      if (isAuditor) {
        return 'Adding, removing and modifying are disabled for the auditor role.';
      }

      return 'This feature is not available.';
    },
    [isAuditor, hasAssume, canViewConversations]
  );

  return (
    <FeatureContext.Provider
      value={{
        ...flags,
        orgMode,
        getTooltip,
        hasFeature,
        loading: orgLoading || adminUserPermLoading,
      }}
    >
      {children}
    </FeatureContext.Provider>
  );
};
